import React, { useState } from 'react';
import SbEditable from 'storyblok-react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Sentry from '@sentry/react';
import InputMask from 'react-input-mask';
import * as Yup from 'yup';

import { leads, metrics } from '../../services';
import TrackFormDirty from '../TrackFormDirty';
import { PhoneRegex } from '../../constants';
import './lead-form-v1.scss';

// I have a decent idea what this is doing. Using https://github.com/formium/formik/blob/master/examples/MultistepWizard.js

const defaultFormValues = {
  name: '',
  email: '',
  phone: '',
};

const ResidentialForm = ({
  title,
  promptText,
  showHomeownerPrompt,
  homeownerPrompt,
  homeownerPromptStyle,
  mode,
}) => (
  <div className="fields">
    {title && <h4 className="title">{title}</h4>}
    {promptText && <p className="step-text">{promptText}</p>}
    {showHomeownerPrompt && homeownerPromptStyle === 'radio' && (
      <div className="prompt">
        <span>{homeownerPrompt}</span>
        <ErrorMessage className="error" component="div" name="isHomeowner" />
        <div className="options">
          <label htmlFor="isHomeownerYes">
            Yes
            <Field type="radio" name="isHomeowner" id="isHomeownerYes" value="true" />
            <span class="checkmark"></span>
          </label>
          <label htmlFor="isHomeownerNo">
            No
            <Field type="radio" name="isHomeowner" id="isHomeownerNo" value="false" />
            <span class="checkmark"></span>
          </label>
        </div>
      </div>
    )}
    {mode === 'tribal' && (
      <div className="field">
        <Field name="tribe" placeholder="Tribe Name" />
        <ErrorMessage className="error" component="div" name="tribe" />
      </div>
    )}
    <div className="field">
      <Field name="name" placeholder="Name" />
      <ErrorMessage className="error" component="div" name="name" />
    </div>
    <div className="field">
      <Field name="phone" placeholder="Phone">
        {({ field }) => (
          <InputMask
            {...field}
            mask="(999) 999-9999"
            placeholder="Phone"
            type="text"
            alwaysShowMask={false}
          />
        )}
      </Field>
      <ErrorMessage className="error" component="div" name="phone" />
    </div>
    <div className="field">
      <Field name="email" placeholder="Email" />
      <ErrorMessage className="error" component="div" type="email" name="email" />
    </div>
    <div className="field">
      <Field name="address" placeholder="Street Address" />
      <ErrorMessage className="error" component="div" name="address" />
    </div>
    <div className="field">
      <Field name="city" placeholder="City" />
      <ErrorMessage className="error" component="div" name="city" />
    </div>
    <div className="field">
      <Field name="zip" placeholder="Zip Code">
        {({ field }) => (
          <InputMask
            {...field}
            mask="99999"
            placeholder="Zip Code"
            type="text"
            alwaysShowMask={false}
          />
        )}
      </Field>
      <ErrorMessage className="error" component="div" name="zip" />
    </div>
    {showHomeownerPrompt && homeownerPromptStyle.startsWith('checkbox') && (
      <div className="field-checkbox">
        <Field name="isHomeowner" type="checkbox" id="isHomeowner" />
        <label htmlFor="isHomeowner">{homeownerPrompt}</label>
      </div>
    )}
    {showHomeownerPrompt && homeownerPromptStyle === 'checkbox_solar_battery' && (
      <>
        <div className="field-checkbox">
          <Field name="existingSolar" type="checkbox" id="existingSolar" />
          <label htmlFor="existingSolar">I already have solar</label>
        </div>
        <div className="field-checkbox">
          <Field name="hasPowerwall" type="checkbox" id="hasPowerwall" />
          <label htmlFor="hasPowerwall">I already have a home battery</label>
        </div>
      </>
    )}
  </div>
);

const CommercialForm = ({ title, promptText, showHomeownerPrompt, homeownerPrompt, mode }) => (
  <div className="fields">
    {title && <h4 className="title">{title}</h4>}
    {promptText && <p className="step-text">{promptText}</p>}
    <div className="field">
      <Field name="company" placeholder="Company/Organization Name" />
      <ErrorMessage className="error" component="div" name="company" />
    </div>
    <div className="field">
      <Field name="name" placeholder="Name" />
      <ErrorMessage className="error" component="div" name="name" />
    </div>
    <div className="field">
      <Field name="phone" placeholder="Phone">
        {({ field }) => (
          <InputMask
            {...field}
            mask="(999) 999-9999"
            placeholder="Phone"
            type="text"
            alwaysShowMask={false}
          />
        )}
      </Field>
      <ErrorMessage className="error" component="div" name="phone" />
    </div>
    <div className="field">
      <Field name="email" placeholder="Email" />
      <ErrorMessage className="error" component="div" type="email" name="email" />
    </div>
    <div className="field">
      <Field name="address" placeholder="Street Address" />
      <ErrorMessage className="error" component="div" name="address" />
    </div>
    <div className="field">
      <Field name="city" placeholder="City" />
      <ErrorMessage className="error" component="div" name="city" />
    </div>
    <div className="field">
      <Field name="zip" placeholder="Zip Code">
        {({ field }) => (
          <InputMask
            {...field}
            mask="99999"
            placeholder="Zip Code"
            type="text"
            alwaysShowMask={false}
          />
        )}
      </Field>
      <ErrorMessage className="error" component="div" name="zip" />
    </div>
    <div className="field">
      <Field as="textarea" name="description" placeholder="Questions/Comments" rows="4" />
      <ErrorMessage className="error" component="div" name="description" />
    </div>
  </div>
);

const TribalPartnerForm = ({ title, promptText }) => (
  <div className="fields">
    {title && <h4 className="title">{title}</h4>}
    {promptText && <p className="step-text">{promptText}</p>}
    <div className="field">
      <Field name="tribe" placeholder="Tribe Name" />
      <ErrorMessage className="error" component="div" name="tribe" />
    </div>
    <div className="field">
      <Field name="name" placeholder="Name" />
      <ErrorMessage className="error" component="div" name="name" />
    </div>
    <div className="field">
      <Field name="phone" placeholder="Phone">
        {({ field }) => (
          <InputMask
            {...field}
            mask="(999) 999-9999"
            placeholder="Phone"
            type="text"
            alwaysShowMask={false}
          />
        )}
      </Field>
      <ErrorMessage className="error" component="div" name="phone" />
    </div>
    <div className="field">
      <Field name="email" placeholder="Email" />
      <ErrorMessage className="error" component="div" type="email" name="email" />
    </div>
    <div className="field">
      <Field as="textarea" name="description" placeholder="Questions/Comments" rows="4" />
      <ErrorMessage className="error" component="div" name="description" />
    </div>
  </div>
);

const LeadFormV1 = (props) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [submitState, setSubmitState] = useState('');
  const [isDirty, setIsDirty] = useState(false);

  const onSubmit = async (values) => {
    setSubmitState('');

    if (!values.email && !values.phone) {
      setSubmitState('failed');
      setErrorMessage('Please provide either your email address or phone number (or both).');
      return;
    }

    try {
      if (mode === 'tribal_partner') {
        await leads.submitPartner({
          ...values,
          type: '',
          referred_by: '',
        });

        metrics.trackPartnerFormSubmit(
          window.location.origin + window.location.pathname,
          'LeadFormV1'
        );
      } else {
        let utmContent = props.blok.utm_content || '';
        const isProd =
          window.location.origin.indexOf('swellenergy.com') > -1 &&
          window.location.origin.indexOf('sbx') === -1;
        const leadSourceAccounts = props.blok.lead_source_account
          ? JSON.parse(props.blok.lead_source_account)
          : { dev: '0016100000fehkAAAQ', prod: '0014M00001k8A7fQAE' };

        await leads.submitLead({
          ...values,
          utmContent,
          leadSourceMedium: props.blok.lead_source_medium || 'Swell Energy',
          leadSource: props.blok.lead_source || 'Online Media',
          leadSourceAccount: isProd ? leadSourceAccounts.prod : leadSourceAccounts.dev,
        });

        metrics.trackLeadFormSubmit(
          window.location.origin + window.location.pathname,
          'LeadFormV1',
          utmContent
        );
      }

      setSubmitState('success');

      if (props.blok.submit_gtag_conversion_event_isaacfrank && window && window.gtag) {
        const gtagId =
          props.blok.isaacfrank_gtag_conversion_id || 'AW-941872757/qxwbCIj559EBEPWsj8ED';
        window.gtag('event', 'conversion', { send_to: gtagId });
      }
    } catch (e) {
      Sentry.captureException(e, {
        lead: values,
      });
      setErrorMessage('We had a problem processing your request. Please try again.');
      setSubmitState('failed');
    }
  };

  const containerClasses = ['lfv1'];
  containerClasses.push('theme-' + props.blok.theme);

  const title = props.blok.title || '';
  const promptText = props.blok.prompt_text || '';
  const submitButtonText = props.blok.submit_button_text || 'Submit';
  const submittingButtonText = props.blok.submitting_button_text || 'Submitting...';
  const buttonStyle = props.blok.button_style || 'blue';
  const showHomeownerPrompt = props.blok.show_homeowner_prompt;
  const homeownerPromptStyle = props.blok.homeowner_prompt_style || 'radio';
  const homeownerPrompt = props.blok.homeowner_prompt || 'I am a homeowner';
  const customerType = props.blok.customer_type;
  const mode = props.blok.mode;
  const successMessage =
    props.blok.success_message ||
    'Thank you! You are one step closer to having affordable and reliable energy. A Swell Energy representative will contact you shortly to discuss your renewable energy options.';

  let modeValidation = {
    name: Yup.string().required('Required'),
    email: Yup.string().email('Enter a valid e-mail address'),
    phone: Yup.string().matches(PhoneRegex, 'Enter your phone number'),
  };

  let modeDefaults = { ...defaultFormValues };

  if (showHomeownerPrompt) {
    if (homeownerPromptStyle === 'checkbox') {
      modeDefaults.isHomeowner = false;
    } else if (homeownerPromptStyle === 'checkbox_solar_battery') {
      modeDefaults.isHomeowner = false;
      modeDefaults.existingSolar = false;
      modeDefaults.hasPowerwall = false;
    } else {
      modeValidation.isHomeowner = Yup.string().required('Required');
      modeDefaults.isHomeowner = '';
    }
  }

  if (mode === 'tribal') {
    modeValidation.tribe = Yup.string().required('Required');
    modeDefaults.tribe = '';
  }

  if (customerType === 'residential') {
    modeValidation.zip = Yup.string()
      .required('Please enter your zip code')
      .matches(/\d{5}/, 'Enter your ZIP code');
    modeDefaults.address = '';
    modeDefaults.city = '';
    modeDefaults.zip = '';
  } else if (customerType === 'commercial') {
    modeValidation.company = Yup.string().required('Required');
    modeDefaults.company = '';
    modeDefaults.description = '';
  } else if (customerType === 'tribal_partner') {
    modeValidation.tribe = Yup.string().required('Required');
    modeDefaults.tribe = '';
    modeDefaults.description = '';
  }

  return (
    <SbEditable content={props.blok}>
      <div className={containerClasses.join(' ')}>
        {submitState !== 'success' && (
          <Formik
            initialValues={modeDefaults}
            onSubmit={onSubmit}
            validationSchema={Yup.object().shape(modeValidation)}
          >
            {(formik) => {
              const buttonText = formik.isSubmitting ? submittingButtonText : submitButtonText;

                return (
                  <Form className="input-form">
                    <TrackFormDirty formComponent='LeadFormV1' isDirty={isDirty} setIsDirty={setIsDirty} />
                    {customerType === 'residential' && <ResidentialForm title={title} promptText={promptText} mode={mode} showHomeownerPrompt={showHomeownerPrompt} homeownerPrompt={homeownerPrompt} homeownerPromptStyle={homeownerPromptStyle} />}
                    {customerType === 'commercial' && <CommercialForm title={title} promptText={promptText} />}
                    {customerType === 'tribal_partner' && <TribalPartnerForm title={title} promptText={promptText} />}
                    {
                      submitState === 'failed' &&
                      <div className='error'>{errorMessage}</div>
                    }
                    <button className={`btn-${buttonStyle}`} disabled={formik.isSubmitting} type="submit">{buttonText}</button>
                    <span className='disclaimer'>
                    By submitting this form, I agree to the{' '}
                      <a href="/privacy-policy">Privacy Policy</a> and consent to be contacted by or
                      on behalf of Swell Energy, including by email, calls, and text messages to any
                      telephone number that I provide, about my interest in Swell Energy products
                      and services. I understand my consent is not required to purchase.
                    </span>
                  </Form>
                )
              }
            }
          </Formik>
        )}
        {submitState === 'success' && <div className="success">{successMessage}</div>}
      </div>
    </SbEditable>
  );
};

export default LeadFormV1;
